import type { User } from 'oidc-client-ts';

import { isDefined } from '../../utils/variables';

/**
 *  Keeping a Zitadel session alive, in the two ways a session can need it.
 *
 *  **Ahead of expiry**, which `oidc-client-ts` does on its own: `automaticSilentRenew` defaults to
 *  on, the `UserManager` constructor starts it, and starting it loads the stored user, which arms a
 *  timer to renew a minute before the access token expires. Nothing here schedules anything —
 *  {@link startZitadelTokenRenewal} exists to *instantiate* the manager on a page that has a
 *  session, and to pass each renewed token on to whoever holds the app's copy of it.
 *
 *  **After the fact**, when a request has already been refused with the token it had:
 *  {@link renewZitadelAccessToken}. Both halves are needed, because the timer cannot help a token
 *  that was already expired when the page loaded.
 *
 *  Renewal is the refresh-token grant, not a hidden iframe: `signinSilent` prefers the refresh
 *  token whenever the stored user has one, so no `silent_redirect_uri` is involved.
 *
 *  Everything here is a no-op for a session that is not a Zitadel one — a legacy or Entra user, or
 *  an environment with no Zitadel configuration at all.
 *
 *  The OIDC machinery is reached through a dynamic import rather than a static one. This module is
 *  wired up from the app-wide authentication state, which every page pulls in — including the public
 *  ones, which have no session to renew and no business loading an authentication library. Loading
 *  it when a session actually needs it keeps that out of everyone else's way.
 */

type RenewalHandlers = {
  /**
   *  Called with a freshly issued access token, both when the renew timer fires and when a sign-in
   *  completes. The caller is expected to store it wherever the app reads the token from.
   */
  onAccessTokenRenewed: (accessToken: string) => void;
  /**
   *  Called when a renewal ahead of expiry failed. Deliberately not the same thing as "the session
   *  is over": see {@link startZitadelTokenRenewal}.
   */
  onRenewalFailed: (error: unknown) => void;
};

/**
 *  The renewal currently in flight, if any.
 *
 *  A dead token tends to be discovered by several requests at once, and each one asking for its own
 *  refresh would spend the refresh token repeatedly and race over which result is stored last.
 *  Sharing one promise means a burst of 401s costs exactly one round trip.
 */
let renewalInFlight: Promise<string | undefined> | undefined;

/**
 *  The `UserManager`, once the module holding it has been loaded — or `undefined` where this
 *  environment has no Zitadel configuration.
 */
const loadZitadelUserManager = async () => {
  try {
    const { tryGetZitadelUserManager } = await import('./zitadel-user-manager');
    return tryGetZitadelUserManager();
  } catch (error) {
    // A chunk that will not load leaves the session exactly as unrenewable as one with no refresh
    // token, and must not surface as an unhandled rejection from the effect that started this.
    console.error('The Zitadel session could not be reached.', error);
    return undefined;
  }
};

const renew = async (): Promise<string | undefined> => {
  const manager = await loadZitadelUserManager();

  // No Zitadel configuration in this environment at all: every session here is a legacy or an
  // Entra one, and there is nothing to renew.
  if (!isDefined(manager)) {
    return undefined;
  }

  try {
    const user = await manager.getUser();

    // No stored user, or one without a refresh token: a legacy or Entra session, or a Zitadel
    // session whose refresh token has been revoked. Nothing to renew with, and asking anyway would
    // send `signinSilent` down its iframe path, which is not configured.
    if (!isDefined(user?.refresh_token)) {
      return undefined;
    }

    const renewed = await manager.signinSilent();
    return renewed?.access_token;
  } catch (error) {
    // Swallowed on purpose: every caller treats "no new token" as the answer, and the caller is the
    // one that decides what that means. The reason goes to the console so a session that ends
    // unexpectedly can be diagnosed.
    console.error('The Zitadel session could not be renewed.', error);
    return undefined;
  }
};

/**
 *  Renews the access token, and returns the new one — or `undefined` when there is nothing to renew
 *  with and when the renewal failed, which a caller cannot usefully tell apart.
 *
 *  Never rejects. Concurrent callers share a single renewal.
 */
export const renewZitadelAccessToken = () => {
  if (!isDefined(renewalInFlight)) {
    const started = renew();
    renewalInFlight = started;
    // Cleared once settled rather than kept, so the next expiry gets a fresh attempt. Safe to leave
    // unhandled: `renew` resolves for every outcome.
    void started.finally(() => {
      if (renewalInFlight === started) {
        renewalInFlight = undefined;
      }
    });
  }

  return renewalInFlight;
};

/**
 *  Arms proactive renewal for this page and reports what it produces. Returns a function that stops
 *  listening again.
 *
 *  A failed renewal is reported but is deliberately *not* treated as the end of the session. The
 *  same event fires for a refresh token that has genuinely expired and for a momentary network
 *  failure while the tab sits idle, and signing someone out over the latter is worse than waiting:
 *  the next request settles the question, and it is the request path that can tell the user why.
 */
export const startZitadelTokenRenewal = ({
  onAccessTokenRenewed,
  onRenewalFailed,
}: RenewalHandlers) => {
  const handleUserLoaded = (user: User) => onAccessTokenRenewed(user.access_token);
  const handleRenewalFailed = (error: unknown) => onRenewalFailed(error);

  // Set by the stop function, so a caller that gives up before the library has finished loading is
  // not left with listeners it can no longer detach.
  let stopped = false;
  let detach: (() => void) | undefined;

  void (async () => {
    // Constructing the manager is the whole of the proactive setup: it starts the silent-renew
    // service, which loads the stored user and arms the timer. A page with no stored Zitadel user
    // arms nothing, and an environment with no Zitadel configuration builds nothing at all.
    const manager = await loadZitadelUserManager();

    if (stopped || !isDefined(manager)) {
      return;
    }

    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addSilentRenewError(handleRenewalFailed);

    detach = () => {
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeSilentRenewError(handleRenewalFailed);
    };
  })();

  return () => {
    stopped = true;
    detach?.();
  };
};

/**
 *  Drops any in-flight renewal. Only intended for tests.
 */
export const resetZitadelTokenRenewal = () => {
  renewalInFlight = undefined;
};
